import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { can } from '@mybusinessmate/schema';
import { TaskFormPage } from '../../../../../../components/tasks/task-form-page';
import { buttonClassName } from '../../../../../../components/ui/button';
import { EmptyState } from '../../../../../../components/ui/empty-state';
import { PageSkeleton } from '../../../../../../components/ui/skeleton';
import { LoadFailure } from '../../../../../../components/ui/state-views';
import { useSession } from '../../../../../../lib/session';
import { isStaffRoleName } from '../../../../../../lib/staff-nav';
import { taskQuery } from '../../../../../../lib/tasks-api';

export const Route = createFileRoute('/subdomain/admin/_staff/tasks/$taskId/edit')({
  // The form is 1200 wide in the design: the brief on the left, triage and the client side on the right.
  staticData: { wide: true },
  component: EditDraftPage,
});

/** Finish a saved draft. A submitted task's brief is version 0 and cannot be edited, so only drafts open here. */
function EditDraftPage() {
  const { taskId } = Route.useParams();
  const { data: session } = useSession();
  const role = session?.user.role;
  const { data: task, isLoading, error, refetch } = useQuery(taskQuery(taskId));

  if (isLoading) return <PageSkeleton />;

  if (!task) {
    return (
      <LoadFailure
        error={error}
        noun="task"
        forbiddenHint="Ask an admin to assign you to it."
        onRetry={() => void refetch()}
      />
    );
  }

  const mayEdit =
    isStaffRoleName(role) && can({ role }, 'task.create', { managesBusiness: role === 'account_manager' });
  if (task.status !== 'User Draft' || !mayEdit) {
    return (
      <div className="rounded-(--mbm-component-radius-card) border border-border-line bg-surface-card">
        <EmptyState
          title={task.status !== 'User Draft' ? 'This brief is locked' : 'You can’t edit this task'}
          description={
            task.status !== 'User Draft'
              ? 'Once a task is submitted its brief is version 0 and stays as it was written. Changes become new versions.'
              : 'Only admins and the business’s account manager can edit a draft.'
          }
          action={
            <Link
              to="/subdomain/admin/tasks/$taskId"
              params={{ taskId }}
              className={buttonClassName({ variant: 'secondary' })}
            >
              Back to the task
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <TaskFormPage
      existing={task}
      project={{
        id: task.projectId,
        name: task.projectName,
        businessId: task.businessId,
        businessName: task.businessName,
      }}
    />
  );
}
